// @ts-nocheck
import { ReactionDisplay } from './ReactionDisplay';
import React from 'react';
import { FormattedText } from './FormattedText';
import { LikeIcon, DownloadIcon } from './Icons';
import { CustomAudioPlayer } from './AudioComponents';
import { Attachment } from '../types';
import { downloadMediaUrl, isTransparentImageUrl } from '../utils/mediaUtils';
import { API_URL } from '../api';

interface ChatBubbleProps {
    text: string;
    author: string;
    pfp?: string;
    themeColor?: string;
    textColor?: string;
    opacity?: number;
    reactions?: { [emoji: string]: string[] };
    currentUser?: string;
    onReactionClick?: (emoji: string) => void;
    onNavigateToProfile?: (username: string) => void;
    onNavigateToHashtag?: (hashtag: string) => void;
    onTouchStart?: (e: any) => void;
    onTouchMove?: (e: any) => void;
    onTouchEnd?: (e: any) => void;
    onMouseDown?: (e: any) => void;
    onMouseMove?: (e: any) => void;
    onMouseUp?: (e: any) => void;
    onMouseLeave?: (e: any) => void;
    onContextMenu?: (e: any) => void;
    depth?: number;
    media?: Attachment[];
}

const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const ChatBubble: React.FC<ChatBubbleProps> = ({
    text,
    author,
    pfp,
    themeColor = '#161b22',
    textColor = '#ffffff',
    opacity = 0.5,
    reactions,
    currentUser,
    onReactionClick,
    onNavigateToProfile,
    onNavigateToHashtag,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    onContextMenu,
    onOpenEmojiPicker,
    depth = 0,
    media
}) => {
    const bubbleBg = hexToRgba(themeColor, opacity);

    const hasReactions = reactions && Object.entries(reactions).some(([_, users]) => users.length > 0);

    const youtubeVideoId = React.useMemo(() => {
        if (!text) return null;
        const match = text.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
        return match ? match[1] : null;
    }, [text]);

    return (
        <div className={`flex items-end group/chat-bubble ${depth > 0 ? (depth === 1 ? 'ml-8 mt-4' : 'ml-6 mt-3') : 'mt-4'} relative`}>
            {depth > 0 && (
                <div className="absolute -left-4 top-0 bottom-0 w-0.5 bg-gray-700/50 rounded-full" style={{ left: '-1rem' }}></div>
            )}
            {pfp && (
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        console.log('ChatBubble pfp clicked for author:', author);
                        onNavigateToProfile?.(author);
                    }} 
                    className="flex-shrink-0 z-20 cursor-pointer focus:outline-none"
                >
                    <img 
                        src={pfp} 
                        alt={author} 
                        className={`${depth > 0 ? 'w-7 h-7' : 'w-9 h-9'} rounded-full mr-2 hover:opacity-80 transition-opacity object-cover`} 
                    />
                </button>
            )}
            <div className="flex-grow flex items-end gap-2 relative">
                {/* Bubble Wrapper with relative positioning for reactions overlay */}
                <div className="relative max-w-[80%] md:max-w-[70%]">
                    <div
                        className="backdrop-blur-sm rounded-[22px] py-2.5 px-4 shadow-md border-none select-none cursor-pointer w-fit break-words text-left"
                        style={{ backgroundColor: bubbleBg, color: textColor }}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        onMouseLeave={onMouseLeave}
                        onContextMenu={onContextMenu}
                    >
                        {text && (
                            <div className="text-sm leading-relaxed" style={{ color: textColor }}>
                                <FormattedText 
                                    text={text} 
                                    onNavigateToProfile={onNavigateToProfile} 
                                    onNavigateToHashtag={onNavigateToHashtag} 
                                    textSize="sm" 
                                    textColor={textColor} 
                                    stickerize 
                                />
                            </div>
                        )}
                        {youtubeVideoId && (
                            <div className="mt-2 rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video w-full max-w-[280px] sm:max-w-[360px]">
                                <iframe
                                    src={`${API_URL}youtube-proxy?videoId=${youtubeVideoId}&origin=${encodeURIComponent(window.location.origin)}`}
                                    className="w-full h-full border-none"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    title="YouTube Video"
                                />
                            </div>
                        )}
                        {media && media.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {media.map((item, idx) => {
                                    const isTransparent = item.type === 'image' && isTransparentImageUrl(item.url, undefined, item.file?.name || item.name);
                                    return (
                                        <div 
                                            key={idx} 
                                            className={`${isTransparent ? 'max-w-[14rem] relative group/chat-media p-1' : 'max-w-[12rem] rounded-xl overflow-hidden bg-black/20 relative group/chat-media'}`}
                                        >
                                            {item.type === 'image' && (
                                                <img 
                                                    src={item.url} 
                                                    className={`max-w-full h-auto cursor-pointer hover:scale-[1.04] transition-transform duration-300 ${isTransparent ? 'drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] filter brightness-105' : ''}`} 
                                                    alt="" 
                                                />
                                            )}
                                            {item.type === 'video' && (
                                                <video 
                                                    src={`${item.url}#t=0.001`} 
                                                    controls 
                                                    className="max-w-full h-auto" 
                                                    playsInline 
                                                    preload="metadata" 
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            )}
                                            {(item.type === 'audio' || item.type === 'voice') && (
                                                <div className="p-2 min-w-[160px]">
                                                    <CustomAudioPlayer src={item.url} authorId={author} />
                                                </div>
                                            )}
                                            {(item.type === 'image' || item.type === 'video' || item.type === 'audio' || item.type === 'voice') && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadMediaUrl(item.url, item.file?.name || `${item.type}-attachment`);
                                                    }}
                                                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/chat-media:opacity-100 transition-all border border-white/10 z-10"
                                                    title="Save media"
                                                >
                                                    <DownloadIcon className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Emoji reactions pill rendered at the bottom-right of the bubble */}
                    {hasReactions && (
                        <div className="absolute -bottom-2 -right-2 flex gap-1 z-10 bg-gray-900/90 backdrop-blur-md px-1.5 py-0.5 rounded-full border border-white/10 shadow-lg scale-90">
                            {reactions && Object.entries(reactions).map(([emoji, users]) => {
                                if (users.length === 0) return null;
                                const isUserReacted = users.includes(currentUser || '');
                                return (
                                    <button
                                        key={emoji}
                                        onClick={() => onReactionClick?.(emoji)}
                                        className={`flex items-center gap-1 px-1 rounded-full text-[9px] font-bold transition-all ${isUserReacted ? 'text-blue-400 scale-105' : 'text-gray-400 hover:text-white'}`}
                                    >
                                        <ReactionDisplay emoji={emoji} className="text-xs" />
                                        {users.length > 1 && <span>{users.length}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Reaction button OUTSIDE the bubble - visible on hover or next to it */}
                <div className="relative group/react-menu flex-shrink-0 self-center opacity-0 group-hover/chat-bubble:opacity-100 transition-opacity">
                    <button className="text-gray-500 hover:text-white transition-all p-1.5 hover:bg-white/10 rounded-full">
                        <LikeIcon className="w-3.5 h-3.5" style={{ color: '#888' }} />
                    </button>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-1 bg-gray-900/95 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl opacity-0 scale-90 group-hover/react-menu:opacity-100 group-hover/react-menu:scale-100 transition-all pointer-events-none group-hover/react-menu:pointer-events-auto flex gap-1.5 z-[100] ring-1 ring-white/10">
                        {['❤️', '🔥', '😂', '😮', '😢', '💯', '👏', '👀'].map(emoji => (
                            <button
                                key={emoji}
                                onClick={() => onReactionClick?.(emoji)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-white/10 hover:scale-110 rounded-xl transition-all text-base"
                            >
                                <ReactionDisplay emoji={emoji} />
                            </button>
                        ))}
                        {onOpenEmojiPicker && (
                            <button
                                onClick={() => onOpenEmojiPicker?.()}
                                className="w-8 h-8 flex items-center justify-center hover:bg-white/10 hover:scale-110 rounded-xl transition-all text-base text-gray-400"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
